import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { EyeOff, LogOut } from "lucide-react";

/**
 * Sticky banner shown across the entire app while an admin is impersonating another user.
 * Reads `_impersonated_by` from the /auth/me response — the backend sets that whenever the
 * shadow `admin_access_token` cookie resolves to a valid admin.
 */
export default function ImpersonationBanner() {
    const { user, refresh } = useAuth();
    const [busy, setBusy] = useState(false);

    if (!user?._impersonated_by) return null;
    const admin = user._impersonated_by;

    const stop = async () => {
        setBusy(true);
        try {
            await api.post("/admin/impersonate/stop");
            await refresh();
            toast.success(`Back to ${admin.name || admin.email}`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="impersonation-banner"
            className="sticky top-0 z-50 w-full bg-red-600 text-white px-4 py-2 flex items-center gap-3 shadow-md">
            <EyeOff size={16} className="shrink-0" />
            <div className="text-sm min-w-0 flex-1">
                <span className="font-bold">Impersonating </span>
                <span className="font-mono truncate">{user.name} · {user.email}</span>
                <span className="opacity-75 hidden sm:inline"> — as admin {admin.name || admin.email}</span>
            </div>
            <button
                onClick={stop}
                disabled={busy}
                data-testid="impersonation-exit"
                className="rounded-full bg-white text-red-700 hover:bg-red-50 text-xs font-bold px-3 py-1.5 flex items-center gap-1.5 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
                <LogOut size={12} /> {busy ? "Exiting…" : "Exit impersonation"}
            </button>
        </div>
    );
}
